import {useMemo} from "react";
import {RootState, useAppSelector} from "../../store";
import {IWinnerTicket} from "../../store/wish/Helper";

/**
 * Check if a specific winner ticket belongs to the current user
 */
export const isWinnerTicketForUser = (winner: IWinnerTicket | null | undefined, userEmail: string | undefined): boolean => {
    if (!winner?.ticket || !userEmail) return false;
    const ticket = winner.ticket;
    const hasRecipient = Boolean(ticket.recipient_email);

    if (hasRecipient) {
        return ticket.recipient_email === userEmail;
    }

    return ticket.user?.email === userEmail;
}

/**
 * Get display name from winner ticket (recipient or user)
 */
export const getWinnerDisplayName = (winner: IWinnerTicket | null | undefined): string => {
    if (!winner?.ticket) return '';
    return winner.ticket.recipient_name ?? winner.ticket.user?.full_name ?? '';
}

/**
 * Get avatar source from winner ticket
 * Returns empty string for recipient tickets, user profile picture otherwise
 */
export const getWinnerAvatarSrc = (winner: IWinnerTicket | null | undefined): string => {
    if (!winner?.ticket) return '';
    const isRecipientTicket = Boolean(winner.ticket.recipient_email);
    return isRecipientTicket ? '' : (winner.ticket.user?.profile_picture ?? '');
}

/**
 * Hook to check if current logged-in user is among the winners
 */
export const useIsUserWinner = (winners: IWinnerTicket[] | null | undefined): boolean => {
    const email = useAppSelector((state: RootState) => state.authReducer.loggedInUser.email);

    return useMemo(() => {
        if (!email || !winners?.length) return false;
        return winners.some(winner => isWinnerTicketForUser(winner, email));
    }, [winners, email]);
}
